import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
// Cognitive Flows' own axios instance — same one SubscriptionManagement.jsx
// (billing, also a plain client/src/pages/*.jsx tab) uses for CF routes: no
// CognitiveFlowsProvider needed here, and its x-cf-workspace header default
// ('account') is harmless, since the server treats these three /mcp-server/
// routes as workspace-exempt (they read only the session's email + shop).
import cfApi, { setCognitiveFlowsApiBase } from '../cognitive-flows/utils/api';
import { accessSummary, sanitizeClientName } from '../cognitive-flows/components/shared/mcpServerTools';

// The user-facing "MCP" section in Account Settings (spec §7.2): the MCP URL,
// copy-paste setup for Claude / ChatGPT / Claude Code, and the signed-in
// user's own connected apps — what each can reach, and Disconnect.
//
// No per-app tool switches (2026-10-06): people manage an app's tools in the
// AI app itself and staff in the Admin Dashboard's MCP Tools tab; switches
// here confused both. Offs saved before were cleared by
// server/scripts/mcp-clear-user-tool-offs.mjs. The tools route is still
// mounted on the server, but nothing here calls it.
//
// Naming note: this is the INBOUND MCP server other apps connect TO. It is
// unrelated to `MCPConnectorsPage` (`client/src/mcp-connectors/`) +
// `services/mcp/`, which is this app acting as an MCP CLIENT to third-party
// servers — deliberately named "McpServerSettings", not anything with
// "Connectors" in it, so the two stay easy to tell apart. It is also
// unrelated to the staff-only "MCP Tools" tab
// (`cognitive-flows/components/dashboard/McpServerToolsManager.jsx`).
//
// Deliberately plain elements, no react-bootstrap: a bootstrap component
// (e.g. `Container`) calls `useBootstrapPrefix`, a hook reading
// `client/node_modules/react`'s context — a SECOND React copy from this
// component's own `tests/node_modules/react` under Vitest/jsdom, which
// throws "Cannot read properties of null (reading 'useContext')" on render.
// McpServerToolsManager.jsx (task A4's staff tab) sidesteps the same trap the
// same way.
//
// Data comes from `server/services/cognitive-flows/mcpConnectedAppsRoutes.js`:
//   GET    /mcp-server/settings            → { mcpUrl, connectedApps, connectedAppsError }
//   DELETE /mcp-server/apps/:grantId
//
// An app's reach is shown from the server's `coverage` (several accounts) or
// `accountName` / `workspaceName` / `workspaceDeleted` (one account), and its
// connection date from `createdAt` (an ISO string) — never looked up or
// converted here.
//
// `clientName` is attacker-controlled (OAuth Dynamic Client Registration lets
// an app register under any name). It is read ONLY through `appDisplayName`
// (the consent page's own sanitizer, shared/mcpServerTools.js) and — the part
// that actually protects — it is only ever shown in a place of its own: the
// card's title, and the "App name" field of the Disconnect confirmation. It
// is never put inside a sentence we wrote, so it cannot forge one. The
// confirmation used to be a native `window.confirm`, which takes ONE string:
// a name that closed its own quotation made its injected sentence read as
// ScaleX's own warning, twice, whatever the sanitizer stripped.
//
// Settings.jsx mounts this section the first time its tab is opened (not with
// the Settings page), so the GET below — and, through it, the Worker — only
// runs for a user who looks at MCP.

const s = {
  // Padded like the Teams tab: the Settings panel itself has no padding.
  page: { display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '860px', padding: '24px 24px 28px' },
  title: { fontWeight: 800, fontSize: '20px', color: 'var(--text-primary, #101828)', margin: 0 },
  subtitle: { color: 'var(--text-secondary, #667085)', fontSize: '14px', margin: '4px 0 0' },
  card: { background: 'var(--surface,#fff)', border: '1px solid var(--border,#EAECF0)', borderRadius: '14px', overflow: 'hidden' },
  cardHeader: { padding: '14px 20px', borderBottom: '1px solid var(--border,#EAECF0)' },
  cardTitle: { fontWeight: 700, fontSize: '15px', color: 'var(--text-primary, #101828)' },
  cardBody: { padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '14px' },
  hint: { fontSize: '13px', color: 'var(--text-secondary, #475467)', lineHeight: 1.6, margin: 0 },
  list: { fontSize: '13px', color: 'var(--text-secondary, #475467)', lineHeight: 1.8, paddingLeft: '18px', margin: 0 },
  codeRow: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  // Every <code> here sets its own colour: Bootstrap's `code` rule is pink (#d63384).
  codeBox: {
    background: '#F9FAFB', border: '1px solid var(--border,#EAECF0)', borderRadius: '8px',
    padding: '8px 12px', fontSize: '13px', wordBreak: 'break-all', flex: '1 1 260px',
    color: 'var(--cux-ink, #132848)',
  },
  codeInline: {
    color: 'var(--cux-ink, #132848)', fontSize: '0.92em', background: '#F2F4F7',
    padding: '1px 5px', borderRadius: '4px',
  },
  copyBtn: { padding: '7px 14px', borderRadius: '8px', fontWeight: 600, fontSize: '12px', cursor: 'pointer', border: '1px solid var(--border,#D0D5DD)', background: '#fff', color: 'var(--text-primary,#344054)' },
  vendorTabs: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  vendorTabBtn: (active) => ({
    padding: '6px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
    border: `1px solid ${active ? 'var(--cux-ink, #132848)' : 'var(--border,#D0D5DD)'}`,
    background: active ? 'var(--cux-ink, #132848)' : '#fff',
    color: active ? '#fff' : 'var(--text-primary,#344054)',
  }),
  errorBox: {
    fontSize: '13px', color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca',
    borderRadius: '10px', padding: '12px 14px', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap',
  },
  retryBtn: { padding: '6px 14px', borderRadius: '8px', fontWeight: 600, fontSize: '12px', cursor: 'pointer', border: '1px solid #fecaca', background: '#fff', color: '#991b1b' },
  empty: { fontSize: '13px', color: 'var(--text-secondary, #667085)', margin: 0 },
  appCard: { border: '1px solid var(--border,#EAECF0)', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' },
  appHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' },
  appName: { fontWeight: 700, fontSize: '14px', color: 'var(--text-primary, #101828)' },
  appMeta: { fontSize: '12px', color: 'var(--text-secondary, #667085)' },
  disconnectBtn: (disabled) => ({ padding: '6px 14px', borderRadius: '8px', fontWeight: 600, fontSize: '12px', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1, border: '1px solid #fecaca', background: '#fff', color: '#b42318', whiteSpace: 'nowrap' }),
  confirmBox: { border: '1px solid #fecaca', background: '#fffbfa', borderRadius: '10px', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '10px' },
  confirmHeading: { fontWeight: 700, fontSize: '14px', color: '#b42318', margin: 0 },
  confirmFields: { display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: '12px', rowGap: '6px', margin: 0, alignItems: 'baseline' },
  confirmLabel: { fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary, #667085)', margin: 0 },
  confirmValue: { fontSize: '13px', color: 'var(--text-primary, #101828)', margin: 0 },
  // The app's self-chosen name: boxed and monospaced, so it reads as a value
  // the app supplied — never as a line of our own text.
  confirmAppName: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: '12px',
    color: 'var(--text-primary, #101828)', background: '#F2F4F7', border: '1px solid var(--border,#D0D5DD)',
    borderRadius: '6px', padding: '3px 8px', margin: 0, wordBreak: 'break-all', justifySelf: 'start',
  },
  confirmActions: { display: 'flex', gap: '8px', justifyContent: 'flex-end' },
  cancelBtn: { padding: '6px 14px', borderRadius: '8px', fontWeight: 600, fontSize: '12px', cursor: 'pointer', border: '1px solid var(--border,#D0D5DD)', background: '#fff', color: 'var(--text-primary,#344054)' },
  confirmDisconnectBtn: (disabled) => ({ padding: '6px 14px', borderRadius: '8px', fontWeight: 600, fontSize: '12px', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1, border: '1px solid #b42318', background: '#b42318', color: '#fff', whiteSpace: 'nowrap' }),
};

// Claude first and the default; Claude Code is for developers, so it comes last.
const VENDORS = [
  { id: 'claude-ai', label: 'Claude' },
  { id: 'chatgpt', label: 'ChatGPT' },
  { id: 'claude-code', label: 'Claude Code' },
];

/** A URL/command box with its own copy button — the one visual pattern the
 * Connect block and the Claude Code tab share. */
function CopyRow({ text, copyLabel, onCopy }) {
  return (
    <div style={s.codeRow}>
      <code style={s.codeBox}>{text}</code>
      <button type="button" style={s.copyBtn} onClick={() => onCopy(text)}>{copyLabel}</button>
    </div>
  );
}

// Verified against code.claude.com/docs/en/mcp-quickstart (2026-09-21): the
// `claude mcp add --transport http <name> <url>` form, then `/mcp` inside a
// session to authenticate a server that needs sign-in.
function ClaudeCodeSetup({ mcpUrl, onCopy }) {
  const cmd = `claude mcp add --transport http almond ${mcpUrl}`;
  return (
    <div>
      <p style={s.hint}>In a terminal — not inside a <code style={s.codeInline}>claude</code> session — run:</p>
      <CopyRow text={cmd} copyLabel="Copy command" onCopy={onCopy} />
      <p style={s.hint}>
        Then start Claude Code with <code style={s.codeInline}>claude</code>, run <code style={s.codeInline}>/mcp</code>, select
        &ldquo;almond&rdquo;, and choose Authenticate to sign in.
      </p>
    </div>
  );
}

// Verified against support.claude.com/en/articles/11175166 (2026-09-21):
// Settings → Connectors → Add custom connector → paste URL → Add → Connect.
function ClaudeAiSetup() {
  return (
    <div>
      <ol style={s.list}>
        <li>In Claude.ai, go to Settings → Connectors (under &ldquo;Customize&rdquo; on some plans).</li>
        <li>Click &ldquo;+&rdquo;, then choose &ldquo;Add custom connector&rdquo;.</li>
        <li>Paste the URL above as the remote MCP server URL, then click &ldquo;Add&rdquo;.</li>
        <li>Click &ldquo;Connect&rdquo; and sign in when prompted.</li>
      </ol>
      <p style={s.hint}>
        Team/Enterprise: an Owner adds the connector once under Organization Settings →
        Connectors before members can connect to it.
      </p>
    </div>
  );
}

// Written from OpenAI's OFFICIAL page, fetched 2026-09-21:
// https://developers.openai.com/api/docs/guides/developer-mode ("How to use").
// Eligibility, the Settings → Security and login switch, and ChatGPT Plugins →
// plus button are its own words; nothing here comes from third-party guides
// (an earlier "Settings → Connectors → Advanced" path did, and was wrong).
// The page lists OAuth among the supported authentication types; that this
// server signs in with OAuth is a fact about THIS server, not a ChatGPT menu.
function ChatGptSetup() {
  return (
    <div>
      <p style={s.hint}>
        Uses ChatGPT developer mode, available to Pro, Plus, Business, Enterprise and
        Education accounts on the web.
      </p>
      <ol style={s.list}>
        <li>In ChatGPT, open Settings → Security and login and turn on Developer mode.</li>
        <li>
          Go to ChatGPT Plugins, select the plus button, and create a developer-mode app for
          this remote MCP server, using the URL above. It signs in with OAuth.
        </li>
        <li>
          In a conversation, choose Developer mode from the composer&apos;s plus menu and pick
          the app. ChatGPT asks you to confirm write actions by default.
        </li>
      </ol>
    </div>
  );
}

/** An app's name, with controls and bidi overrides removed — the only way
 * `clientName` is ever read for display. Shown only in a place of its own
 * (see the file header), never inside a sentence we wrote. */
const appDisplayName = (app) => sanitizeClientName(app?.clientName, 'Unnamed app');

// The Disconnect confirmation's own words. CONSTANT: they never contain the
// app's name (or anything else an app or a user typed), which is shown
// separately in the labelled "App name" field — so no name can add a
// sentence that reads as ours. Access ends "within about a minute", not
// "immediately": a revoked token can keep working that long (KV reads are
// edge-cached ~60 s).
const DISCONNECT_HEADING = 'Disconnect this app?';
const DISCONNECT_EXPLANATION = 'The app below will lose access to your Almond data within about a minute. '
  + 'To use it with Almond again, you will need to connect it again.';
// A coverage app is ONE connection across several accounts (spec 2026-09-24).
const DISCONNECT_COVERAGE_NOTE = 'This app is connected to more than one account. Disconnecting removes it from all of them.';

/** Where the app's grant reaches, from the server's three workspace states. A
 * workspace name is typed by the account's own users, so it is shown through
 * the same sanitizer as an app name, and likewise only in its own field. */
function workspaceLabel(app) {
  if (!app?.workspaceId) return 'Entire account';
  if (app.workspaceDeleted) return 'Deleted workspace';
  return sanitizeClientName(app.workspaceName, 'Unnamed workspace');
}

/** The app's connection date for display, or null. `createdAt` is an ISO
 * 8601 string from the server (or null) — the server already converted the
 * OAuth library's Unix seconds, so it is parsed here as-is. */
function connectedDate(app) {
  const date = app?.createdAt ? new Date(app.createdAt) : null;
  if (!date || Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** What the app can reach: "1 account: Cadence OTC" (plus its workspace when
 * the grant is workspace-scoped), or the coverage summary for several
 * accounts. Account and workspace names are typed by users, so each goes
 * through the same sanitizer as an app name. */
function accessLabel(app) {
  if (app?.coverage) return accessSummary(app);
  const account = sanitizeClientName(app?.accountName, 'this account');
  return app?.workspaceId ? `1 account: ${account} (workspace: ${workspaceLabel(app)})` : `1 account: ${account}`;
}

function connectedLabel(app) {
  const date = connectedDate(app);
  return date ? `Connected ${date}` : 'Connection date unknown';
}

/**
 * The in-page Disconnect confirmation, inside the app's own card. Our heading
 * and explanation are constants; the app's name, workspace and connection
 * date each sit in their own labelled field. Focus lands on Cancel, and
 * Escape cancels.
 */
function DisconnectConfirmation({ app, busy, onCancel, onConfirm }) {
  const cancelRef = useRef(null);
  useEffect(() => { cancelRef.current?.focus(); }, []);
  const headingId = `mcp-disconnect-heading-${app.grantId}`;
  const explanationId = `mcp-disconnect-explanation-${app.grantId}`;

  return (
    <div
      role="alertdialog"
      aria-labelledby={headingId}
      aria-describedby={explanationId}
      style={s.confirmBox}
      onKeyDown={(e) => { if (e.key === 'Escape' && !busy) onCancel(); }}
    >
      <p id={headingId} style={s.confirmHeading}>{DISCONNECT_HEADING}</p>
      <p id={explanationId} style={s.hint}>{DISCONNECT_EXPLANATION}</p>
      {app.coverage && <p style={s.hint}>{DISCONNECT_COVERAGE_NOTE}</p>}
      <dl style={s.confirmFields}>
        <dt style={s.confirmLabel}>App name</dt>
        <dd style={s.confirmAppName} data-testid="mcp-disconnect-app-name">{appDisplayName(app)}</dd>
        <dt style={s.confirmLabel}>Access</dt>
        <dd style={s.confirmValue} data-testid="mcp-disconnect-workspace">{accessLabel(app)}</dd>
        <dt style={s.confirmLabel}>Connected</dt>
        <dd style={s.confirmValue} data-testid="mcp-disconnect-connected">{connectedDate(app) || 'Unknown'}</dd>
      </dl>
      <div style={s.confirmActions}>
        <button ref={cancelRef} type="button" style={s.cancelBtn} disabled={busy} onClick={onCancel}>
          Cancel
        </button>
        <button type="button" style={s.confirmDisconnectBtn(busy)} disabled={busy} onClick={onConfirm}>
          {busy ? 'Disconnecting…' : 'Disconnect'}
        </button>
      </div>
    </div>
  );
}

function AppCard({ app, disconnecting, onDisconnect }) {
  const name = appDisplayName(app);
  const busyDisconnect = Boolean(disconnecting[app.grantId]);
  const [confirming, setConfirming] = useState(false);
  // Cancel hands focus back to the Disconnect button that opened the
  // confirmation, rather than dropping it on the page body.
  const disconnectRef = useRef(null);
  const wasConfirming = useRef(false);
  useEffect(() => {
    if (wasConfirming.current && !confirming) disconnectRef.current?.focus();
    wasConfirming.current = confirming;
  }, [confirming]);

  return (
    <div style={s.appCard} data-testid={`mcp-connected-app-${app.grantId}`}>
      <div style={s.appHead}>
        <div>
          <div style={s.appName}>{name}</div>
          <div style={s.appMeta}>Access: {accessLabel(app)} · {connectedLabel(app)}</div>
        </div>
        {!confirming && (
          <button
            ref={disconnectRef}
            type="button"
            style={s.disconnectBtn(busyDisconnect)}
            disabled={busyDisconnect}
            onClick={() => setConfirming(true)}
          >
            Disconnect
          </button>
        )}
      </div>

      {confirming && (
        <DisconnectConfirmation
          app={app}
          busy={busyDisconnect}
          onCancel={() => setConfirming(false)}
          onConfirm={() => onDisconnect(app)}
        />
      )}
    </div>
  );
}

export default function McpServerSettings({ app_url } = {}) {
  useEffect(() => {
    if (app_url) setCognitiveFlowsApiBase(app_url);
  }, [app_url]);

  const [settings, setSettings] = useState(null); // { mcpUrl, connectedApps, connectedAppsError }
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [appsReloading, setAppsReloading] = useState(false);
  const [activeVendor, setActiveVendor] = useState('claude-ai');
  const [disconnecting, setDisconnecting] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const { data } = await cfApi.get('/mcp-server/settings');
      setSettings(data);
    } catch (err) {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // "Try again" on the Connected apps block reloads only that block — the
  // Connect block (URL + setup) stays on screen instead of collapsing into
  // the whole page's loading state.
  const reloadApps = useCallback(async () => {
    setAppsReloading(true);
    try {
      const { data } = await cfApi.get('/mcp-server/settings');
      setSettings((prev) => ({
        ...prev,
        connectedApps: data?.connectedApps ?? null,
        connectedAppsError: data?.connectedAppsError ?? null,
      }));
    } catch (err) {
      setSettings((prev) => ({
        ...prev,
        connectedApps: null,
        connectedAppsError: prev?.connectedAppsError || { code: 'REQUEST_FAILED' },
      }));
    } finally {
      setAppsReloading(false);
    }
  }, []);

  const copy = useCallback(async (text) => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch (err) {
      toast.error('Couldn’t copy — select the text and copy it yourself.');
    }
  }, []);

  // Called by the card's own in-page confirmation — see DisconnectConfirmation.
  const handleDisconnect = useCallback(async (app) => {
    setDisconnecting((m) => ({ ...m, [app.grantId]: true }));
    try {
      await cfApi.delete(`/mcp-server/apps/${encodeURIComponent(app.grantId)}`);
      setSettings((prev) => ({
        ...prev,
        connectedApps: Array.isArray(prev?.connectedApps)
          ? prev.connectedApps.filter((a) => a.grantId !== app.grantId)
          : prev?.connectedApps ?? null,
      }));
      // Our words only — the card, and the name with it, has just gone.
      toast.success('App disconnected.');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to disconnect.');
    } finally {
      setDisconnecting((m) => {
        const next = { ...m };
        delete next[app.grantId];
        return next;
      });
    }
  }, []);

  return (
    <div style={s.page}>
      <div>
        <h2 style={s.title}>MCP</h2>
        <p style={s.subtitle}>Connect Claude, ChatGPT and other AI apps to Almond.</p>
      </div>

      <div style={s.card}>
        <div style={s.cardHeader}><span style={s.cardTitle}>Connect</span></div>
        <div style={s.cardBody}>
          {loading ? (
            <p style={s.empty}>Loading…</p>
          ) : loadFailed || !settings ? (
            <div style={s.errorBox}>
              <span>We couldn&apos;t load your MCP settings right now.</span>
              <button type="button" style={s.retryBtn} onClick={load}>Try again</button>
            </div>
          ) : !settings.mcpUrl ? (
            <p style={s.hint}>MCP isn&apos;t set up on this environment yet.</p>
          ) : (
            <>
              <CopyRow text={settings.mcpUrl} copyLabel="Copy URL" onCopy={copy} />
              <div style={s.vendorTabs}>
                {VENDORS.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    style={s.vendorTabBtn(activeVendor === v.id)}
                    aria-pressed={activeVendor === v.id}
                    onClick={() => setActiveVendor(v.id)}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
              {activeVendor === 'claude-ai' && <ClaudeAiSetup />}
              {activeVendor === 'chatgpt' && <ChatGptSetup />}
              {activeVendor === 'claude-code' && <ClaudeCodeSetup mcpUrl={settings.mcpUrl} onCopy={copy} />}
            </>
          )}
        </div>
      </div>

      {!loading && settings && settings.mcpUrl && (
        <div style={s.card}>
          <div style={s.cardHeader}><span style={s.cardTitle}>Connected apps</span></div>
          <div style={s.cardBody}>
            {appsReloading ? (
              <p style={s.empty}>Loading…</p>
            ) : settings.connectedAppsError ? (
              <div style={s.errorBox}>
                <span>We couldn&apos;t load your connected apps right now.</span>
                <button type="button" style={s.retryBtn} onClick={reloadApps}>Try again</button>
              </div>
            ) : !Array.isArray(settings.connectedApps) || settings.connectedApps.length === 0 ? (
              <p style={s.empty}>No apps connected yet.</p>
            ) : (
              settings.connectedApps.map((app) => (
                <AppCard
                  key={app.grantId}
                  app={app}
                  disconnecting={disconnecting}
                  onDisconnect={handleDisconnect}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
